/** Shared data hooks. Server state goes through the query cache; offline data through storage. */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ApiError, qs } from '../lib/api/client';
import type { MyWork, Paged, PackSite, PackTemplateVersion, SiteListItem, SurveyDetail } from '../lib/api/types';
import type { DraftRecord } from '../lib/offline/types';
import { useQuery } from '../lib/query';
import { getTemplateVersion, mergeDueSites, withCurrentDue, withCurrentDueWork } from '../lib/sync/pack';
import type { SyncStatus } from '../lib/sync/sync-service';
import { useServices } from './services';

export function useSyncStatus(): SyncStatus {
  const { sync } = useServices();
  return useSyncExternalStore(
    useCallback((l) => sync.subscribe(l), [sync]),
    () => sync.getStatus(),
  );
}

/**
 * Re-run an async loader when its inputs change and, unless `onDraft` is null,
 * when a draft it cares about changes (offline data). Capture saves a draft on
 * every keystroke, so loaders that don't depend on drafts must not listen.
 */
function useOfflineData<T>(
  load: () => Promise<T>,
  deps: unknown[],
  onDraft: ((draftId: string) => boolean) | null = () => true,
): { data: T | undefined; loading: boolean; reload: () => void } {
  const { drafts } = useServices();
  const [state, setState] = useState<{ data: T | undefined; loading: boolean }>({ data: undefined, loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!onDraft) return;
    return drafts.subscribe((id) => {
      if (onDraft(id)) setTick((t) => t + 1);
    });
  }, [drafts, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    load().then(
      (data) => alive && setState({ data, loading: false }),
      () => alive && setState((s) => ({ ...s, loading: false })),
    );
    return () => {
      alive = false;
    };
  }, [tick, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, reload: () => setTick((t) => t + 1) };
}

/** One draft, reloaded only when that draft changes. */
export function useDraft(id: string | undefined) {
  const { drafts } = useServices();
  return useOfflineData<DraftRecord | undefined>(() => (id ? drafts.get(id) : Promise.resolve(undefined)), [id], (changed) => changed === id);
}

export function useDrafts() {
  const { drafts } = useServices();
  return useOfflineData<DraftRecord[]>(() => drafts.list(), []);
}

/** Sites from the device, with days to follow-up worked out for today (never frozen offline, ADR-0014). */
export function usePackSite(id: string | undefined) {
  const { storage } = useServices();
  // Not tied to drafts: the capture screen would otherwise re-read the site on every keystroke.
  return useOfflineData<PackSite | undefined>(() => (id ? storage.getSite(id).then((s) => s && withCurrentDue(s)) : Promise.resolve(undefined)), [id], null);
}

export function usePackSites() {
  const { storage } = useServices();
  return useOfflineData<PackSite[]>(() => storage.listSites().then((all) => all.map((s) => withCurrentDue(s))), []);
}

export function useTemplateVersion(versionId: string | undefined) {
  const { storage, api } = useServices();
  const online = useOnline();
  // From the device, else fetched (e.g. an analyst opening a survey on desktop) and cached.
  // A version never changes once published, so draft changes don't reload it.
  const q = useOfflineData<PackTemplateVersion | undefined>(
    () => (versionId ? getTemplateVersion(api, storage, versionId) : Promise.resolve(undefined)),
    [versionId],
    null,
  );
  // Not on the device and the fetch failed (offline): try again once the device is back online.
  const failed = !!versionId && !q.loading && !q.data;
  useEffect(() => {
    if (online && failed) q.reload();
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  return q;
}

const MY_WORK_META = 'myWork';

/**
 * GET /dashboard/my-work. Due dates are copied onto the device's sites and the
 * last answer is kept, so Home still works offline (`data.offline` is then true).
 */
export function useMyWork() {
  const { api, storage } = useServices();
  return useQuery({
    queryKey: ['dashboard', 'my-work'],
    staleTime: 60_000,
    queryFn: async (): Promise<MyWork & { offline?: boolean }> => {
      try {
        const w = await api.get<MyWork>('/dashboard/my-work');
        await storage.setMeta(MY_WORK_META, w);
        if (w.reporter?.dueSites) await mergeDueSites(storage, w.reporter.dueSites);
        return w;
      } catch (e) {
        const cached = e instanceof ApiError && e.isNetwork ? await storage.getMeta<MyWork>(MY_WORK_META) : undefined;
        if (cached) return { ...withCurrentDueWork(cached), offline: true };
        throw e;
      }
    },
  });
}

export function useSiteSearch(q: string, enabled: boolean) {
  const { api } = useServices();
  return useQuery({
    queryKey: ['sites', 'search', q],
    queryFn: () => api.get<Paged<SiteListItem>>(`/sites${qs({ q, pageSize: 25 })}`),
    enabled: enabled && q.trim().length >= 2,
  });
}

/** GET /surveys/:id (online only; audited on the server). */
export function useSurveyDetail(id: string | undefined, enabled = true) {
  const { api } = useServices();
  return useQuery({
    queryKey: ['surveys', id],
    queryFn: () => api.get<SurveyDetail>(`/surveys/${encodeURIComponent(id!)}`),
    enabled: !!id && enabled,
  });
}

/** Debounce a fast-changing value (search boxes). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    useCallback((l) => {
      window.addEventListener('online', l);
      window.addEventListener('offline', l);
      return () => {
        window.removeEventListener('online', l);
        window.removeEventListener('offline', l);
      };
    }, []),
    () => navigator.onLine !== false,
  );
}
